import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { db, auth } from './firebase';
import './OutstandingFeesReport.css';

function OutstandingFeesReport({ onClose, onRecordPayment }) {
  const [students, setStudents] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterSchool, setFilterSchool] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('amount-desc');
  const [filtersCollapsed, setFiltersCollapsed] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    applyFiltersAndSort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [students, filterSchool, searchTerm, sortBy]);

  // Calculate TERM FEES owing (excluding admin) - WITH OVERRIDE SUPPORT
  const calculateTermFeesOwing = (student, termStructure) => {
    if (!student || !termStructure || !termStructure.termDates) return 0;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const studentStartDate = student.startDate?.toDate
      ? student.startDate.toDate()
      : new Date(student.startDate);
    studentStartDate.setHours(0, 0, 0, 0);

    const currentYear = today.getFullYear();
    let totalTermFees = 0;

    const termFee = student.feeStructure?.standardFee || 0;
    const feeOverrides = student.feeOverrides || [];

    for (let termNum = 1; termNum <= 4; termNum++) {
      const termData = termStructure.termDates[`term${termNum}`];
      if (!termData || !termData.start || !termData.end) continue;

      const [startMonth, startDay] = termData.start.split('-').map(Number);
      const [endMonth, endDay] = termData.end.split('-').map(Number);

      const termStart = new Date(currentYear, startMonth - 1, startDay);
      termStart.setHours(0, 0, 0, 0);

      const termEnd = new Date(currentYear, endMonth - 1, endDay);
      termEnd.setHours(23, 59, 59, 999);

      const shouldCharge = (
        studentStartDate <= termEnd &&
        today >= termStart &&
        (student.isActive ||
          (student.deactivationInfo?.deactivatedDate &&
            new Date(student.deactivationInfo.deactivatedDate) > termStart))
      );

      if (shouldCharge) {
        const override = feeOverrides.find(
          o => o.termNumber === termNum &&
          (o.overrideType === 'term' || o.overrideType === 'both')
        );

        if (override && override.termFee !== undefined) {
          totalTermFees += override.termFee;
        } else {
          totalTermFees += termFee;
        }
      }
    }

    // Sum actual payments from termPayments (where QuickPayment stores them)
    const tp = student.termPayments || {};
    const termFeesPaid = (tp.term1 || 0) + (tp.term2 || 0) + (tp.term3 || 0) + (tp.term4 || 0);
    totalTermFees -= termFeesPaid;

    return Math.max(0, totalTermFees);
  };

  // Calculate outstanding PER TERM (each term on its own) + which terms have started.
  // Returns: { perTerm: {term1,term2,term3,term4}, serviced: {term1..term4} }
  // "serviced" = the term has started (today is on/after the term start date),
  // so the CSV can show a column for it. The owing amount for each term is the
  // amount charged for that term minus the payment recorded against that term.
  const calculatePerTermOwing = (student, termStructure) => {
    const result = {
      perTerm: { term1: 0, term2: 0, term3: 0, term4: 0 },
      serviced: { term1: false, term2: false, term3: false, term4: false }
    };

    if (!student || !termStructure || !termStructure.termDates) return result;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const studentStartDate = student.startDate?.toDate
      ? student.startDate.toDate()
      : new Date(student.startDate);
    studentStartDate.setHours(0, 0, 0, 0);

    const currentYear = today.getFullYear();
    const termFee = student.feeStructure?.standardFee || 0;
    const feeOverrides = student.feeOverrides || [];
    const tp = student.termPayments || {};

    for (let termNum = 1; termNum <= 4; termNum++) {
      const termData = termStructure.termDates[`term${termNum}`];
      if (!termData || !termData.start || !termData.end) continue;

      const [startMonth, startDay] = termData.start.split('-').map(Number);
      const [endMonth, endDay] = termData.end.split('-').map(Number);

      const termStart = new Date(currentYear, startMonth - 1, startDay);
      termStart.setHours(0, 0, 0, 0);

      const termEnd = new Date(currentYear, endMonth - 1, endDay);
      termEnd.setHours(23, 59, 59, 999);

      // Has this term started yet? If so we show a column for it.
      result.serviced[`term${termNum}`] = today >= termStart;

      const shouldCharge = (
        studentStartDate <= termEnd &&
        today >= termStart &&
        (student.isActive ||
          (student.deactivationInfo?.deactivatedDate &&
            new Date(student.deactivationInfo.deactivatedDate) > termStart))
      );

      let charged = 0;
      if (shouldCharge) {
        const override = feeOverrides.find(
          o => o.termNumber === termNum &&
          (o.overrideType === 'term' || o.overrideType === 'both')
        );
        charged = (override && override.termFee !== undefined) ? override.termFee : termFee;
      }

      const paid = tp[`term${termNum}`] || 0;
      result.perTerm[`term${termNum}`] = Math.max(0, charged - paid);
    }

    return result;
  };

  // Calculate ADMIN FEE owing separately - WITH OVERRIDE SUPPORT
  const calculateAdminFeeOwing = (student) => {
    const feeOverrides = student.feeOverrides || [];

    const adminOverride = feeOverrides.find(
      o => o.overrideType === 'admin' || o.overrideType === 'both'
    );

    let adminFee;
    if (adminOverride && adminOverride.adminFee !== undefined) {
      adminFee = adminOverride.adminFee;
    } else {
      adminFee = student.adminFee || 0;
    }

    // Read admin payment from termPayments (where QuickPayment stores it)
    const adminFeePaid = student.termPayments?.admin || 0;
    return Math.max(0, adminFee - adminFeePaid);
  };

  const loadData = async () => {
    try {
      const user = auth.currentUser;
      const franchiseeRef = doc(db, 'Franchisees', user.uid);

      // Load schools
      const schoolsQuery = query(
        collection(db, 'schools'),
        where('franchiseeRef', '==', franchiseeRef),
        where('isActive', '==', true)
      );
      const schoolsSnapshot = await getDocs(schoolsQuery);
      const schoolsList = schoolsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setSchools(schoolsList);

      // Load ALL students (active AND inactive) to calculate owing
      const studentsQuery = query(
        collection(db, 'students'),
        where('franchiseeRef', '==', franchiseeRef)
      );

      const studentsSnapshot = await getDocs(studentsQuery);

      // Calculate real-time owing for each student
      const studentsWithCalculatedOwing = await Promise.all(
        studentsSnapshot.docs.map(async (docSnap) => {
          const studentData = { id: docSnap.id, ...docSnap.data() };

          // Get school and term structure
          if (studentData.schoolRef) {
            try {
              const schoolDoc = await getDoc(studentData.schoolRef);
              if (schoolDoc.exists()) {
                const schoolData = schoolDoc.data();

                if (schoolData.termStructureRef) {
                  const termStructureDoc = await getDoc(schoolData.termStructureRef);
                  if (termStructureDoc.exists()) {
                    const termStructure = termStructureDoc.data();

                    // Calculate separate owing amounts
                    const termFeesOwing = calculateTermFeesOwing(studentData, termStructure);
                    const adminFeeOwing = calculateAdminFeeOwing(studentData);
                    const breakdown = calculatePerTermOwing(studentData, termStructure);

                    return {
                      ...studentData,
                      termFeesOwing: termFeesOwing,
                      adminFeeOwing: adminFeeOwing,
                      calculatedOwing: termFeesOwing + adminFeeOwing,
                      perTermOwing: breakdown.perTerm,
                      termServiced: breakdown.serviced,
                      _hasCalculation: true
                    };
                  }
                }
              }
            } catch (error) {
              console.error('Error calculating owing for student:', studentData.id, error);
            }
          }

          // Fallback if calculation fails
          return {
            ...studentData,
            termFeesOwing: studentData.termFeesOwing || 0,
            adminFeeOwing: calculateAdminFeeOwing(studentData),
            calculatedOwing: (studentData.termFeesOwing || 0) + calculateAdminFeeOwing(studentData),
            perTermOwing: { term1: 0, term2: 0, term3: 0, term4: 0 },
            termServiced: { term1: false, term2: false, term3: false, term4: false },
            _hasCalculation: false
          };
        })
      );

      // Filter to only students who owe money
      const studentsWithDebt = studentsWithCalculatedOwing.filter(
        student => (student.calculatedOwing || 0) > 0
      );

      setStudents(studentsWithDebt);
      setLoading(false);
    } catch (error) {
      console.error('Error loading data:', error);
      alert('Error loading outstanding fees report');
      setLoading(false);
    }
  };

  const applyFiltersAndSort = () => {
    let filtered = [...students];

    // School filter
    if (filterSchool !== 'all') {
      filtered = filtered.filter(s => s.schoolRef?.id === filterSchool);
    }

    // Search filter
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(s => {
        const name = s.studentName?.toLowerCase() || '';
        const account = s.accountNumber?.toLowerCase() || '';
        const contact = s.primaryContact?.name?.toLowerCase() || '';
        return name.includes(search) || account.includes(search) || contact.includes(search);
      });
    }

    // Sort
    switch (sortBy) {
      case 'amount-desc':
        filtered.sort((a, b) => (b.calculatedOwing || 0) - (a.calculatedOwing || 0));
        break;
      case 'amount-asc':
        filtered.sort((a, b) => (a.calculatedOwing || 0) - (b.calculatedOwing || 0));
        break;
      case 'name-asc':
        filtered.sort((a, b) => {
          const nameA = a.studentName?.toLowerCase() || '';
          const nameB = b.studentName?.toLowerCase() || '';
          return nameA.localeCompare(nameB);
        });
        break;
      case 'name-desc':
        filtered.sort((a, b) => {
          const nameA = a.studentName?.toLowerCase() || '';
          const nameB = b.studentName?.toLowerCase() || '';
          return nameB.localeCompare(nameA);
        });
        break;
      default:
        break;
    }

    setFilteredStudents(filtered);
  };

  const formatCurrency = (amount) => {
    return `R${(amount || 0).toFixed(2)}`;
  };

  const formatDate = (date) => {
    if (!date) return 'Never';
    if (date.toDate) {
      return date.toDate().toLocaleDateString();
    }
    return new Date(date).toLocaleDateString();
  };

  const getTotalOwing = () => {
    return filteredStudents.reduce((sum, s) => sum + (s.calculatedOwing || 0), 0);
  };

  // Which term numbers should appear as columns:
  // any term that has been serviced (started) for at least one student shown.
  const getActiveTerms = () => {
    return [1, 2, 3, 4].filter(n =>
      filteredStudents.some(s => s.termServiced?.[`term${n}`])
    );
  };

  const exportToCSV = () => {
    const activeTerms = getActiveTerms();

    // Build the headings:
    // School | Student | T1 os | T2 os | ... | Admin os | Total os | Parent Name | Parent Number
    const headers = [
      'School',
      'Student',
      ...activeTerms.map(n => `T${n} os`),
      'Admin os',
      'Total os',
      'Parent Name',
      'Parent Number'
    ];

    // Group the CSV by school, then by student name, so each franchisee can read it school-by-school.
    const sortedForExport = [...filteredStudents].sort((a, b) => {
      const schoolA = (a.schoolName || '').toLowerCase();
      const schoolB = (b.schoolName || '').toLowerCase();
      if (schoolA !== schoolB) return schoolA.localeCompare(schoolB);
      const nameA = (a.studentName || '').toLowerCase();
      const nameB = (b.studentName || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });

    const rows = sortedForExport.map(student => {
      const perTerm = student.perTermOwing || {};
      const termValues = activeTerms.map(n => (perTerm[`term${n}`] || 0).toFixed(2));
      const adminOS = student.adminFeeOwing || 0;
      const termTotal = activeTerms.reduce((sum, n) => sum + (perTerm[`term${n}`] || 0), 0);
      const totalOS = termTotal + adminOS;

      return [
        student.schoolName || 'N/A',
        student.studentName || 'N/A',
        ...termValues,
        adminOS.toFixed(2),
        totalOS.toFixed(2),
        student.primaryContact?.name || 'N/A',
        student.primaryContact?.cellPhone || 'N/A'
      ];
    });

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `outstanding-fees-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (loading) {
    return <div className="loading">Loading outstanding fees report...</div>;
  }

  return (
    <div className="outstanding-fees-container">
      <div className="report-header">
        <div className="header-top">
          <h1>💰 Outstanding Fees Report</h1>

        </div>

        <button
          onClick={() => setFiltersCollapsed(!filtersCollapsed)}
          style={{
            background: '#4169E1',
            color: 'white',
            border: 'none',
            padding: '10px 20px',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '600',
            width: '100%',
            marginBottom: '15px'
          }}
        >
          {filtersCollapsed ? '▲ Show Filters & Summary' : '▼ Hide Filters & Summary'}
        </button>

        {!filtersCollapsed && (
          <>
        <div className="filters-container">
          <div className="filter-group">
            <label>School:</label>
            <select
              value={filterSchool}
              onChange={(e) => setFilterSchool(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Schools</option>
              {schools.map(school => (
                <option key={school.id} value={school.id}>
                  {school.schoolName}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Sort By:</label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="filter-select"
            >
              <option value="amount-desc">Highest Owing First</option>
              <option value="amount-asc">Lowest Owing First</option>
              <option value="name-asc">Name (A-Z)</option>
              <option value="name-desc">Name (Z-A)</option>
            </select>
          </div>

          <div className="filter-group search-group">
            <label>Search:</label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Name, Account #, Contact..."
              className="search-input"
            />
          </div>

          <div className="filter-group">
            <label>&nbsp;</label>
            <button onClick={exportToCSV} className="btn-export">
              📥 Export to CSV
            </button>
          </div>
        </div>

        <div className="summary-bar">
          <div className="summary-item">
            <span className="summary-label">Total Students with Debt:</span>
            <span className="summary-value">{filteredStudents.length}</span>
          </div>
          <div className="summary-item highlight">
            <span className="summary-label" style={{ color: 'white' }}>Total Outstanding:</span>
            <span className="summary-value" style={{ color: 'white' }}>{formatCurrency(getTotalOwing())}</span>
          </div>
        </div>
        </>
        )}
      </div>

      {filteredStudents.length === 0 ? (
        <div className="no-results">
          <div className="no-results-icon">🎉</div>
          <h2>No Outstanding Fees!</h2>
          <p>All students matching your filters are paid up.</p>
        </div>
      ) : (
        <div className="report-table-container">
          <table className="report-table">
            <thead>
              <tr>
                <th>School</th>
                <th>Student</th>
                {getActiveTerms().map(n => (
                  <th key={`term-head-${n}`}>T{n} os</th>
                ))}
                <th>Admin os</th>
                <th>Total os</th>
                <th>Parent Name</th>
                <th>Parent Number</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredStudents.map(student => {
                const activeTerms = getActiveTerms();
                const perTerm = student.perTermOwing || {};
                const adminOS = student.adminFeeOwing || 0;
                const termTotal = activeTerms.reduce((sum, n) => sum + (perTerm[`term${n}`] || 0), 0);
                const totalOS = termTotal + adminOS;
                return (
                  <tr key={student.id} className={!student.isActive ? 'inactive-row' : ''}>
                    <td>{student.schoolName || 'N/A'}</td>
                    <td>
                      <div className="student-name-cell">
                        {student.studentName}
                        {!student.isActive && (
                          <span className="inactive-indicator">🚫 Inactive</span>
                        )}
                      </div>
                    </td>
                    {activeTerms.map(n => (
                      <td key={`term-${student.id}-${n}`} className="amount-cell owing">
                        {formatCurrency(perTerm[`term${n}`] || 0)}
                      </td>
                    ))}
                    <td className="amount-cell owing">{formatCurrency(adminOS)}</td>
                    <td className="amount-cell owing">
                      <strong>{formatCurrency(totalOS)}</strong>
                    </td>
                    <td>{student.primaryContact?.name || 'N/A'}</td>
                    <td>{student.primaryContact?.cellPhone || 'N/A'}</td>
                    <td className="actions-cell">
                      <button
                        onClick={() => onRecordPayment(student.id)}
                        className="btn-payment"
                        title="Record Payment"
                      >
                        💵 Pay
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="total-row">
                <td colSpan={2 + getActiveTerms().length + 1}><strong>TOTAL OUTSTANDING:</strong></td>
                <td className="amount-cell owing">
                  <strong>{formatCurrency(getTotalOwing())}</strong>
                </td>
                <td colSpan="3"></td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}

export default OutstandingFeesReport;